import { format } from 'date-fns'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { CardCheckbox } from '@/components/ui/CardCheckbox'
import { DateInput } from '@/components/ui/DateInput'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { parseError } from '../../../../../lib/errors'
import { formatDate, parseDateOnly } from '../../../../../lib/formatters'
import { cancelPolicy, type InsurancePolicy } from '../../../../../models/insurancePolicy'

enum CancelScope {
  WHOLE_POLICY = 'WHOLE_POLICY',
  COVERAGES = 'COVERAGES',
}

const SCOPE_OPTIONS = [
  { value: CancelScope.WHOLE_POLICY, label: 'Whole policy' },
  { value: CancelScope.COVERAGES, label: 'Specific coverages' },
]

interface CancelPolicyDialogProps {
  orgId: string
  policy: InsurancePolicy
  onOpenChange: (open: boolean) => void
  onCanceled: () => void
}

/** Staff cancel a policy, or some of its coverages, as of a date within its term. */
export function CancelPolicyDialog({ orgId, policy, onOpenChange, onCanceled }: CancelPolicyDialogProps) {
  const [scope, setScope] = useState(CancelScope.WHOLE_POLICY)
  const [cancelDate, setCancelDate] = useState<Date | undefined>()
  const [coverageIds, setCoverageIds] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const coveragesInForce = policy.coverages.filter((coverage) => !coverage.dateCanceled)
  const isCoverageScope = scope === CancelScope.COVERAGES
  const canSubmit = !!cancelDate && (!isCoverageScope || coverageIds.length > 0)

  const toggleCoverage = (coverageId: string, checked: boolean) =>
    setCoverageIds((current) =>
      checked ? [...current, coverageId] : current.filter((id) => id !== coverageId)
    )

  const handleSubmit = async () => {
    if (!cancelDate) return
    setIsSaving(true)
    try {
      await cancelPolicy(
        orgId,
        policy.id,
        format(cancelDate, 'yyyy-MM-dd'),
        isCoverageScope ? coverageIds : []
      )
      toast.success(isCoverageScope ? 'Coverage canceled.' : 'Policy canceled.')
      onCanceled()
      onOpenChange(false)
    } catch (err) {
      toast.error(parseError(err))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel policy {policy.vendorId || policy.idShort}</DialogTitle>
          <DialogDescription>
            Coverage ends on the date you pick. Pick a date from {formatDate(policy.dateStart)} to{' '}
            {formatDate(policy.dateEnd)}.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-5">
          {/* One coverage left in force can still be canceled alone while another is only scheduled to end. */}
          {policy.coverages.length > 1 && coveragesInForce.length > 0 && (
            <SegmentedControl
              value={scope}
              onValueChange={(value) => setScope(value as CancelScope)}
              options={SCOPE_OPTIONS}
            />
          )}
          {isCoverageScope && (
            <div className="flex flex-col gap-2">
              <Label>Coverages to cancel</Label>
              {coveragesInForce.map((coverage) => (
                <CardCheckbox
                  key={coverage.id}
                  size="sm"
                  isChecked={coverageIds.includes(coverage.id)}
                  onChange={(checked) => toggleCoverage(coverage.id, checked)}
                >
                  {coverage.label}
                </CardCheckbox>
              ))}
            </div>
          )}
          <div className="flex flex-col gap-2">
            <Label>Cancellation date</Label>
            <DateInput
              label="Select date"
              value={cancelDate}
              onChange={setCancelDate}
              defaultMonth={new Date()}
              hidden={[
                { before: parseDateOnly(policy.dateStart)! },
                { after: parseDateOnly(policy.dateEnd)! },
              ]}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Keep policy
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={isSaving ? 'Canceling…' : undefined}
          >
            {isCoverageScope ? 'Cancel coverage' : 'Cancel policy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
